<template>
    <BasicTabs :tabs="tabs" :tab-key="tabKey" :title="$t('vue.settings')">
        <slot />
    </BasicTabs>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import {
    UserIcon,
    UserGroupIcon,
    LockClosedIcon,
    EnvelopeIcon,
    ClockIcon,
} from "@heroicons/vue/24/solid";
import route from "ziggy-js";
import type TabItem from "@/types/Settings/TabItem";
import { useTranslations } from "@/composables/useTranslations";
import { Settings } from "lucide-vue-next";
import { usePage } from "@inertiajs/vue3";
import type { IRhizomPageProps } from "@/types/RhizomPageProps/RhizomPageProps.ts";
import usePermissions from "@/composables/usePermissions";

defineProps({
    tabKey: {
        type: String,
        default: "profile",
    },
});

const { t } = useTranslations();
const page = usePage<IRhizomPageProps>();
const { isOwner, isAdmin } = usePermissions();

const isProfileEdit = computed(
    () => route().current() === "settings.profile-settings.index",
);

const isMailingPreferences = computed(
    () => route().current() === "settings.mailing-preferences.index",
);

const isSecuritySettings = computed(
    () => route().current() === "settings.security-settings.index",
);

const isProfileSection = computed(
    () =>
        isProfileEdit.value ||
        isMailingPreferences.value ||
        isSecuritySettings.value,
);

const tabs: TabItem[] = isProfileSection.value
    ? [
          {
              title: t("vue.profile"),
              key: "profile-settings",
              icon: UserIcon,
              url: route("settings.profile-settings.index"),
          },
          {
              title: t("vue.mailing_preferences"),
              key: "mailing-preferences",
              icon: EnvelopeIcon,
              url: route("settings.mailing-preferences.index"),
          },
          {
              title: t("vue.password_and_security"),
              key: "security-settings",
              icon: LockClosedIcon,
              url: route("settings.security-settings.index"),
          },
      ]
    : (() => {
          const links: TabItem[] = [
              {
                  title: t("vue.team_settings"),
                  key: "team-settings",
                  icon: UserGroupIcon,
                  url: route("settings.team-settings.index"),
              },
              {
                  title: t("vue.team_settings_api_tokens"),
                  key: "team-settings-api-tokens",
                  icon: Settings,
                  url: route("settings.api-tokens.index"),
              },
          ];

          if (page.props.hasFreePlan) {
              links.splice(1, 1);
          }

          if (isOwner.value || isAdmin.value) {
              links.push({
                  title: t("vue.team_activity_log"),
                  key: "team-activity-log",
                  icon: ClockIcon,
                  url: route("settings.team-activity-log.index"),
              });
          }

          return links;
      })();
</script>
